import { useState, useRef, useEffect } from 'react';
import type { DutchVerb, VerbTense } from '@homeapp/shared';
import { TENSE_LABELS } from '@homeapp/shared';

interface Props {
  verb: DutchVerb;
  tenseQuestion: VerbTense;
  sentenceWithBlank?: string;
  onSubmit: (answer: string) => void;
  onExplain: (word: string) => void;
  disabled?: boolean;
}

export default function IrregularVerbExercise({ verb, tenseQuestion, sentenceWithBlank, onSubmit, onExplain, disabled }: Props) {
  const [answer, setAnswer] = useState('');
  const [answers, setAnswers] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Randomly show Dutch or English prompt
  const [showDutch] = useState(() => Math.random() < 0.5);

  // Count blanks in sentence
  const blankCount = sentenceWithBlank ? (sentenceWithBlank.match(/___/g) || []).length : 0;
  const hasMultipleBlanks = blankCount > 1;

  useEffect(() => {
    setAnswer('');
    setAnswers(new Array(blankCount).fill(''));
    inputRef.current?.focus();
  }, [verb.verbId, tenseQuestion, blankCount]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasMultipleBlanks) {
      const combined = answers.filter(a => a.trim()).join(' ');
      if (combined) onSubmit(combined);
    } else {
      if (answer.trim()) onSubmit(answer.trim());
    }
  };

  const updateAnswer = (index: number, value: string) => {
    setAnswers(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-sm text-gray-500 uppercase tracking-wide mb-1">
          Onregelmatige Werkwoorden
        </div>
        <div className="text-2xl font-bold text-gray-900">
          {showDutch ? verb.infinitive : verb.english}
        </div>
        <div className="text-lg text-gray-600">
          {showDutch ? `(${verb.english})` : `(${verb.infinitive})`}
        </div>
      </div>

      {/* Tense label */}
      <div className="text-center">
        <span className="inline-block bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-medium">
          {TENSE_LABELS[tenseQuestion]}
        </span>
      </div>

      {/* Context sentence with inline input */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {sentenceWithBlank ? (
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="text-sm text-gray-500 mb-2">Vul in:</div>
            <div className="text-lg text-gray-700 leading-relaxed inline">
              {sentenceWithBlank.split(/___/).map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <input
                      ref={i === 0 ? inputRef : undefined}
                      type="text"
                      value={hasMultipleBlanks ? (answers[i] || '') : answer}
                      onChange={(e) => hasMultipleBlanks ? updateAnswer(i, e.target.value) : setAnswer(e.target.value)}
                      disabled={disabled}
                      className="inline-block border-b-2 border-orange-400 bg-transparent text-center text-lg font-medium text-gray-900 focus:border-orange-600 focus:outline-none w-32 mx-1 py-0"
                      placeholder="..."
                      autoComplete="off"
                      autoCapitalize="off"
                    />
                  )}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <input
              ref={inputRef}
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              disabled={disabled}
              className="border-b-2 border-orange-400 bg-transparent text-center text-xl font-medium text-gray-900 focus:border-orange-600 focus:outline-none w-64 py-1"
              placeholder="Type the form..."
              autoComplete="off"
              autoCapitalize="off"
            />
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={disabled || (hasMultipleBlanks ? answers.every(a => !a.trim()) : !answer.trim())}
            className="flex-1 bg-orange-600 text-white py-3 px-6 rounded-xl font-medium text-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Check ✓
          </button>
          <button
            type="button"
            onClick={() => onExplain(verb.infinitive)}
            className="bg-gray-200 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-300 transition-colors"
            title="Get explanation"
          >
            💡
          </button>
        </div>
      </form>

      {/* Difficulty badge */}
      <div className="text-center">
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          Difficulty: {Array.from({ length: verb.difficulty }, (_, i) => (
            <span key={i}>⭐</span>
          ))}
        </span>
      </div>
    </div>
  );
}
